import React from 'react';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {TargetFile} from 'folder-items/FileConstants';
import SelectorOptionInput from 'metadata/SelectorOptionInput';
// @ts-expect-error
// eslint-disable-next-line unused-imports/no-unused-imports
import {Entry, EntrySummary} from 'notebook/ELNConstants';
import {BioentityLinkSchemaField, FieldType, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import BioentityLinkInput from 'schemas/tags/BioentityLinkInput';
import {getValueNodesFactory} from 'search/filters/base/StandardFilterFormComponents';
import {SchemaFieldFilterFormProps} from 'search/filters/SchemaFieldStandardFilterForm';
import {SearchContext, SortKey, StandardFilterDataType} from 'search/SearchConstants';
import {capitalize, typeToName} from 'util/NameUtil';

import {SchemaFieldFilterFormSkeletonProps} from './base/SchemaFieldFilterFormSkeleton';

interface MultiLinkSchemaFieldFilterFormProps
  extends
    Omit<SchemaFieldFilterFormSkeletonProps, 'valueNode' | 'operatorDropdown'>,
    SchemaFieldFilterFormProps {
  onChangeValue: (value: Array<string>) => void;
  selectedSchemaField: SchemaField;
  value: Array<string>;
}

export function MultiLinkSchemaFieldFilterValueNode(props: MultiLinkSchemaFieldFilterFormProps): JSX.Element {
  const selectedIds = props.value ? props.value : [];
  const selectedItems = selectedIds.map((id) => ({id}));
  let searchInput;
  switch (props.selectedSchemaField.fieldType) {
    case FieldType.ENTRY_LINK:
      searchInput = getValueNodesFactory(StandardFilterDataType.MULTI_VALUE_ENTITY_LINK)({
        ...props,
        linkedTypes: [ModelTypeConstants.ENTRY],
      })[0];
      break;
    case FieldType.BATCH_LINK: {
      searchInput = getValueNodesFactory(StandardFilterDataType.MULTI_VALUE_ENTITY_LINK)({
        ...props,
        linkedTypes: [ModelTypeConstants.BATCH],
      })[0];
      break;
    }
    case FieldType.STORABLE_LINK:
      searchInput = getValueNodesFactory(StandardFilterDataType.MULTI_VALUE_ENTITY_LINK)({
        ...props,
        linkedTypes: [
          ModelTypeConstants.LOCATION,
          ModelTypeConstants.CONTAINER,
          ModelTypeConstants.BOX,
          ModelTypeConstants.PLATE,
        ],
      })[0];
      break;
    case FieldType.AA_PART_LINK:
    case FieldType.BASE_MOLECULE_LINK:
    case FieldType.BIOENTITY_LINK:
    case FieldType.PART_LINK:
    case FieldType.TRANSCRIPTION_LINK:
    case FieldType.TRANSLATION_LINK: {
      const linkSchemaField = props.selectedSchemaField as BioentityLinkSchemaField;
      let placeholder;
      if (linkSchemaField.requiredLink.folderItemType) {
        placeholder = capitalize(typeToName(linkSchemaField.requiredLink.folderItemType));
      } else if (linkSchemaField.requiredLink.tagSchema) {
        placeholder = linkSchemaField.requiredLink.tagSchema.name;
      } else {
        placeholder = 'Entity';
      }

      // TODO: convert this to using standard filter form components
      searchInput = (
        <BioentityLinkInput
          placeholder={placeholder}
          onChange={({targetFiles}: {targetFiles: Array<TargetFile>}) => {
            props.onChangeValue(targetFiles.map(({id}) => id));
          }}
          tag={{
            name: props.selectedSchemaField.name,
            tagSchemaField: props.selectedSchemaField,
            // This is a hack - BioentityLinkInput is not designed to take Array<{id: string}> in targetFiles
            // @ts-expect-error
            targetFiles: selectedItems,
          }}
          shouldAllowMultiple={true}
          context={SearchContext.LISTING}
        />
      );
      break;
    }
    case FieldType.BLOB_LINK:
      searchInput = getValueNodesFactory(StandardFilterDataType.MULTI_VALUE_ENTITY_LINK)({
        ...props,
        linkedTypes: [ModelTypeConstants.BLOB],
        // Amgen in particular wants to be able to find the blob with the highest-numbered suffix,
        // so we sort in reverse order by name. Ideally we would have substring search for run field values
        // to make this easier.
        sorts: [{key: SortKey.SORT_NAME_KEY, reverse: true}],
      })[0];
      break;
    default:
      assert(
        props.selectedSchemaField.fieldType === FieldType.SELECTOR,
        `Unsupported field type ${props.selectedSchemaField.fieldType}`
      );
      searchInput = (
        <SelectorOptionInput
          defaultValue={{
            name: props.selectedSchemaField.name,
            schemaFieldSelectorOptionIds: selectedIds,
            tagSchemaField: props.selectedSchemaField,
          }}
          onChange={({schemaFieldSelectorOptionIds}: {schemaFieldSelectorOptionIds: Array<string>}) => {
            props.onChangeValue(schemaFieldSelectorOptionIds);
          }}
          schemaFieldSelectorId={props.selectedSchemaField.schemaFieldSelectorId}
          shouldAllowMultiple={true}
        />
      );
      break;
  }

  return searchInput;
}
